/**
 * Named form design tokens for Wagamour V2.
 *
 * Describes the existing approved dark-glass language — do not redesign it.
 * Screens must not invent placeholder opacity, form hex, or one-off error surfaces.
 *
 * Canonical sources (reuse; do not fork when a source already exists):
 * - homeV2Text / homeV2Eyebrow* — typography + Profile V2 field label geometry
 * - surfaceForeground — disabled FG + status destructive
 * - v2ModalContent — field fill/border, field classNames, chip helpers, error banner
 *
 * Phase 2A: error text + label geometry conflicts resolved in this contract.
 * Primitive drift (ProfileV2Status local red; modal 10px labels) is deferred migration debt.
 *
 * @see docs/FORM_DESIGN_SYSTEM.md
 * @see docs/V2_MODAL_DESIGN_STANDARD.md
 */

import type { CSSProperties } from 'react'
import {
  homeV2Eyebrow,
  homeV2EyebrowClass,
  homeV2EyebrowStyle,
  homeV2Text,
} from '@/lib/home/homeV2Theme'
import {
  SURFACE_FG_DARK_DISABLED,
  SURFACE_FG_LIGHT,
  SURFACE_FG_LIGHT_DISABLED,
  SURFACE_FG_LIGHT_MUTED,
  SURFACE_FG_STATUS,
} from '@/lib/design/surfaceForeground'
import {
  v2ModalErrorBannerStyle,
  v2ModalFieldBaseClassName,
  v2ModalFieldStyle,
  v2ModalInputClassName,
  v2ModalStyles,
  v2ModalTextareaClassName,
} from '@/lib/design/v2ModalContent'
import { colors } from '@/styles/designSystem'

/**
 * Placeholder on dark glass — matches `placeholder:text-white/50` in v2ModalFieldBaseClassName.
 * Quieter than value text; readable on iPhone over champagne glass.
 * Defined once here because no prior named constant existed (only a Tailwind class fragment).
 */
export const FORM_PLACEHOLDER_DARK_GLASS = 'rgba(255, 255, 255, 0.50)' as const

/** Tailwind class fragment already used by V2 fields — screens must not invent /55, /45, or light gray placeholders. */
export const FORM_PLACEHOLDER_CLASS_NAME = 'placeholder:text-white/50' as const

/** Disabled control opacity — matches `disabled:opacity-60` on v2ModalFieldBaseClassName. */
export const FORM_DISABLED_OPACITY = 0.6 as const

/**
 * Read-only field opacity — matches ProfileV2Input `readOnlyStyle` (0.85).
 * Named once; Profile is the only System A read-only field treatment.
 */
export const FORM_READ_ONLY_OPACITY = 0.85 as const

/** Amber focus ring — matches `focus:ring-amber-400/45`. */
export const FORM_FOCUS_RING = 'rgba(251, 191, 36, 0.45)' as const

/** Amber focus border — matches `focus:border-amber-200/40`. */
export const FORM_FOCUS_BORDER = 'rgba(253, 230, 138, 0.40)' as const

/** Unselected chip label — matches `text-white/80` in v2ModalChipClassName. */
export const FORM_CHIP_UNSELECTED_TEXT = 'rgba(255, 255, 255, 0.80)' as const

/** Unselected chip border — matches `border-white/15`. */
export const FORM_CHIP_UNSELECTED_BORDER = 'rgba(255, 255, 255, 0.15)' as const

/** Unselected chip fill — matches `bg-white/[0.06]`. */
export const FORM_CHIP_UNSELECTED_SURFACE = 'rgba(255, 255, 255, 0.06)' as const

/** Selected chip border — matches `border-amber-200/55`. */
export const FORM_CHIP_SELECTED_BORDER = 'rgba(253, 230, 138, 0.55)' as const

/** Selected chip fill — matches `bg-amber-400/32`. */
export const FORM_CHIP_SELECTED_SURFACE = 'rgba(251, 191, 36, 0.32)' as const

/**
 * Profile V2 field-label geometry (canonical).
 * Traced from ProfileV2Label → homeV2EyebrowClass + homeV2Eyebrow().
 * Modal FieldLabel/Eyebrow 10px variants are deferred alignment debt — not alternate SSOT.
 */
export const FORM_LABEL_CLASS_NAME = homeV2EyebrowClass
export const FORM_LABEL_LETTER_SPACING = homeV2Eyebrow().letterSpacing
export const FORM_LABEL_FONT_SIZE_PX = 11 as const

/** Structured roles derived from `v2ModalErrorBannerStyle()` — never light solid red error boxes. */
const formErrorSurfaceFromBanner = (() => {
  const banner = v2ModalErrorBannerStyle()
  return {
    background: String(banner.background),
    border: String(banner.border),
    /** Prefer shared status token; banner color matches SURFACE_FG_STATUS.destructive. */
    color: SURFACE_FG_STATUS.destructive,
  }
})()

/**
 * Phase 2A resolutions — single canonical contract for previously conflicting roles.
 * Primitive files may still drift until a later migration; consumers must use these tokens.
 */
export const formTokenResolutions = {
  errorText: {
    status: 'resolved' as const,
    canonical: 'SURFACE_FG_STATUS.destructive',
    rationale:
      'Prefer shared surfaceForeground status architecture over ProfileV2Status local red. Matches V2 error banner foreground.',
    deferredDebt:
      'ProfileV2Status tone=error still hardcodes a local red until ProfileV2Ui may be updated.',
  },
  fieldLabelGeometry: {
    status: 'resolved' as const,
    canonical:
      'ProfileV2Label → homeV2EyebrowClass (11px) + homeV2Eyebrow letterSpacing 0.14em',
    rationale:
      'Profile V2 is the approved form SSOT. homeV2Eyebrow documents Standard V2 eyebrow as 11px uppercase with 0.14em tracking.',
    deferredDebt:
      'WagamourV2ModalFieldLabel (10px/0.12em) and WagamourV2ModalEyebrow (10px/0.14em) remain until modal content may be aligned.',
  },
} as const

/**
 * @deprecated Use formTokenResolutions. Kept so older Phase 1 tests/docs references resolve during transition.
 */
export const formTokenConflicts = formTokenResolutions

/**
 * Dark-glass form roles — complete Phase 2A contract (no open System A token conflicts).
 * Describes Profile / V2 field language; does not change production visuals by itself.
 */
export const formTokensDarkGlass = {
  // —— Typography ——
  value: homeV2Text.primary,
  placeholder: FORM_PLACEHOLDER_DARK_GLASS,
  /** Color — homeV2Text.section (Profile / modal eyebrow color). */
  label: homeV2Text.section,
  /**
   * Geometry — Profile V2 eyebrow (11px / 0.14em).
   * Use labelClassName + labelLetterSpacing / formLabelStyle(); do not invent 10px variants.
   */
  labelClassName: FORM_LABEL_CLASS_NAME,
  labelFontSizePx: FORM_LABEL_FONT_SIZE_PX,
  labelLetterSpacing: FORM_LABEL_LETTER_SPACING,
  helper: homeV2Text.metadata,
  helperMuted: homeV2Text.muted,
  metadata: homeV2Text.metadata,
  /** Destructive foreground — SURFACE_FG_STATUS.destructive (not ProfileV2Status local red). */
  error: SURFACE_FG_STATUS.destructive,
  required: homeV2Text.warm,
  disabled: SURFACE_FG_DARK_DISABLED,
  /** Read-only keeps value color; dim via readOnlyOpacity. */
  readOnly: homeV2Text.primary,

  // —— Field surfaces ——
  inputSurface: v2ModalStyles.field.background,
  inputBorder: v2ModalStyles.field.borderColor,
  focusBorder: FORM_FOCUS_BORDER,
  focusRing: FORM_FOCUS_RING,
  disabledSurface: v2ModalStyles.field.background,
  /** Banner surface; text color forced to shared destructive token. */
  errorSurface: formErrorSurfaceFromBanner,

  // —— Choice controls (ProfileV2Chip ↔ WagamourV2ModalChipToggle agree) ——
  selectedChip: {
    borderColor: FORM_CHIP_SELECTED_BORDER,
    background: FORM_CHIP_SELECTED_SURFACE,
  },
  unselectedChip: {
    borderColor: FORM_CHIP_UNSELECTED_BORDER,
    background: FORM_CHIP_UNSELECTED_SURFACE,
  },
  selectedChipText: homeV2Text.headline,
  unselectedChipText: FORM_CHIP_UNSELECTED_TEXT,

  // —— State ——
  readOnlyOpacity: FORM_READ_ONLY_OPACITY,
  disabledOpacity: FORM_DISABLED_OPACITY,
} as const

/**
 * Light / elevated form surface — Phase 1 stub only.
 * Marketing, contact, waitlist, privacy, consent, provider verification.
 * Complete lightElevated bindings in a later phase; do not mix into darkGlass.
 */
export const formTokensLightElevated = {
  surface: 'lightElevated' as const,
  /** Incomplete — light path remains on ui/Input until a dedicated light form kit. */
  complete: false as const,
  value: SURFACE_FG_LIGHT,
  placeholder: colors.textSecondary,
  label: SURFACE_FG_LIGHT,
  helper: SURFACE_FG_LIGHT_MUTED,
  helperMuted: SURFACE_FG_LIGHT_MUTED,
  metadata: SURFACE_FG_LIGHT_MUTED,
  disabled: SURFACE_FG_LIGHT_DISABLED,
  /** Explicitly not dark-glass white/50. */
  notes:
    'Phase 1 stub: lightElevated reuses surfaceForeground light roles only. No dark-glass tokens here.',
} as const

export const formTokens = {
  darkGlass: formTokensDarkGlass,
  lightElevated: formTokensLightElevated,
} as const

export type FormTokens = typeof formTokens
export type FormTokensDarkGlass = typeof formTokensDarkGlass

/** Inline style for a dark-glass text field (value color + glass fill). */
export function formFieldStyle(): CSSProperties {
  return v2ModalFieldStyle()
}

/** Profile V2 field-label style — color + 0.14em tracking via homeV2EyebrowStyle. */
export function formLabelStyle(): CSSProperties {
  return homeV2EyebrowStyle()
}

/** Error banner style — delegates to canonical V2 modal error surface. */
export function formErrorSurfaceStyle(): CSSProperties {
  return {
    ...v2ModalErrorBannerStyle(),
    color: SURFACE_FG_STATUS.destructive,
  }
}

/** Class helpers — prefer these over inventing placeholder / focus / label utilities. */
export const formTokenClassNames = {
  placeholder: FORM_PLACEHOLDER_CLASS_NAME,
  fieldBase: v2ModalFieldBaseClassName,
  input: v2ModalInputClassName,
  textarea: v2ModalTextareaClassName,
  label: FORM_LABEL_CLASS_NAME,
} as const
